import { formatTime, toBerlinDateKey } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Button } from '../../../ds/components';
import { daysBetween, daysText, relativeDay, relativeDayTime, todayKey } from '../format';
import type { Tone, UpcomingItem } from '../types';
import { KindChip, OriginChip } from './bits';
import styles from './record.module.css';

export interface UpcomingBlockProps {
  items: UpcomingItem[];
  note: string | null;
  /** The task the page was opened for (`?task=`), shown highlighted. */
  highlightId: string | null;
  busyId: string | null;
  onAction: (item: UpcomingItem, action: string) => void;
  /** A task without an editor of its own (`?task=`) gets „Erledigt" as an extra action. */
  completeId?: string | null | undefined;
}

/** The kind chip of an item: "Aufgabe", "Termin"/"Videotermin", "E-Mail · geplant" (from the meta line). */
export function upcomingKind(item: UpcomingItem): { label: string; tone: Tone; meta: string | null } {
  if (item.kind === 'message') {
    const match = /^(.+? · geplant)(?: · (.*))?$/.exec(item.meta ?? '');
    return { label: match?.[1] ?? 'geplant', tone: 'info', meta: match?.[2] ?? null };
  }
  if (item.kind === 'appointment') {
    const video = /Videotermin/.test(item.meta ?? '');
    return {
      label: video ? 'Videotermin' : 'Termin',
      tone: video ? 'accent' : 'info',
      meta: (item.meta ?? '').replace(/ · Videotermin$/, '') || null,
    };
  }
  return { label: 'Aufgabe', tone: item.overdue ? 'danger' : 'accent', meta: item.meta };
}

/** "Heute, 09:00" · "Überfällig seit 1 Tag" · "Versand morgen, 08:00" · "noch nicht terminiert". */
export function upcomingWhen(item: UpcomingItem): string {
  if (item.at === null) return 'noch nicht terminiert';
  if (item.kind === 'message')
    return `Versand ${relativeDayTime(item.at).replace(/^./, (c) => c.toLowerCase())}`;
  if (item.kind === 'task' && item.overdue) {
    const days = Math.max(1, daysBetween(toBerlinDateKey(item.at), todayKey()));
    return `Überfällig seit ${daysText(days)}`;
  }
  const time = formatTime(item.at);
  return time === '00:00' && item.kind === 'task' ? relativeDay(item.at) : relativeDayTime(item.at);
}

/** „Jetzt senden" is the primary action of a planned message (T2); „Abbrechen" its quiet second one. */
function actionVariant(key: string): 'primary' | 'secondary' | 'ghost' {
  if (key === 'cancel') return 'ghost';
  return key === 'send_now' ? 'primary' : 'secondary';
}

/** The „Steht an" block above the timeline: open tasks, booked appointments, scheduled messages. */
export function UpcomingBlock({
  items,
  note,
  highlightId,
  busyId,
  onAction,
  completeId = null,
}: UpcomingBlockProps) {
  if (items.length === 0 && !note) return null;
  return (
    <div className={styles.upcoming}>
      <div className={styles.upcomingHead}>
        <span className={styles.eyebrowSmall}>Steht an</span>
        <span className={styles.upcomingCount}>{items.length > 0 ? `${items.length} offen` : ''}</span>
      </div>
      {items.length > 0 ? (
        <div className={styles.upcomingList}>
          {items.map((item) => {
            const kind = upcomingKind(item);
            const actions = [...item.actions].sort((a, b) =>
              a.key === 'cancel' ? -1 : b.key === 'cancel' ? 1 : 0,
            );
            if (
              completeId === item.id &&
              item.kind === 'task' &&
              !actions.some((action) => action.key === 'complete')
            ) {
              actions.push({ key: 'complete', label: 'Erledigt' });
            }
            return (
              <div
                key={item.id}
                className={clsx(
                  styles.upcomingItem,
                  item.kind === 'message' && styles.upcomingItemPlanned,
                  item.blocked && styles.upcomingItemBlocked,
                  highlightId === item.id && styles.upcomingItemHighlight,
                )}
              >
                <KindChip tone={item.blocked ? 'warning' : kind.tone}>{kind.label}</KindChip>
                <div className={styles.upcomingText}>
                  <div className={styles.upcomingTitle}>{item.title}</div>
                  <div className={styles.metaRow}>
                    <span className={clsx(styles.upcomingDate, item.overdue && styles.upcomingDateOverdue)}>
                      {upcomingWhen(item)}
                    </span>
                    {item.origin ? <OriginChip label={item.origin} /> : null}
                    {kind.meta ? <span className={styles.metaText}>{kind.meta}</span> : null}
                  </div>
                </div>
                <span className={styles.upcomingActions}>
                  {item.blocked ? (
                    <Button variant="ghost" size="small" disabled>
                      Blockiert
                    </Button>
                  ) : (
                    actions.map((action) => (
                      <Button
                        key={action.key}
                        variant={actionVariant(action.key)}
                        size="small"
                        loading={busyId === `${item.id}:${action.key}`}
                        onClick={() => onAction(item, action.key)}
                      >
                        {action.label}
                      </Button>
                    ))
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
      {note ? <p className={styles.upcomingNote}>{note}</p> : null}
    </div>
  );
}
